export var Features;
(function (Features) {
    // long-lived
    Features["IDDQD"] = "iddqd";
    Features["SCALE_MODE"] = "scale-mode";
    Features["XYLEM"] = "xylem";
    Features["FORCE_XYLEM_SSO"] = "force-xylem-sso";
    Features["POD_PILOT"] = "pod-pilot";
    // development
    Features["DISABLE_AUTO_REFRESH"] = "disable-auto-refresh";
    Features["BENCHMARKS"] = "benchmarks";
    Features["DISABLE_RAW"] = "disable-raw";
    Features["MAGNITUDE_HIGHER_MAP_DEVICES"] = "magnitude-higher-map-devices";
    Features["PERFORMANCE_OVERVIEW"] = "performance-overview";
    // maintenance
    Features["OVERRIDE_MAINTENANCE_MODE"] = "override-maintenance-mode";
    Features["UPDATE_BANNER"] = "update-banner";
    Features["PAYMENT_BANNER"] = "payment-banner";
    // advanced (in-use by customers)
    Features["CLUSTERS"] = "clusters";
    // features
    Features["TRACK_ACCOUNT_TIME"] = "track-account-time";
    // integrations (enabled per-account)
    Features["INTEGRATIONS_HUBSPOT"] = "integrations-hubspot";
    Features["INTEGRATIONS_SALESFORCE_SANDBOX"] = "integrations-salesforce-sandbox";
    // Migration to pod-pilot
    Features["POD_PILOT_REDIRECT"] = "pod-pilot-redirect";
    Features["POD_PILOT_NO_REDIRECT"] = "pod-pilot-no-redirect";
})(Features || (Features = {}));
function isFeature(f) {
    if (typeof f !== 'string')
        return false;
    return Object.values(Features).some((v) => v.toString() === f);
}
function isLocalFeature(f) {
    if (!f || typeof (f) !== 'object')
        return false;
    return isFeature(f.name) && f.enabled !== undefined;
}
// local storage keys
const LOCAL_FEATURE_STORAGE_KEY = 'toggles';
const IGNORE_LOCAL_FEATURE_STORAGE_KEY = 'toggle-pilot-disabled';
// compile-time overrides for features enabled across all users and accounts
const COMPILED_FEATURES = {
    [Features.UPDATE_BANNER]: false,
    [Features.POD_PILOT]: process.env.VUE_APP_STAGE?.includes('podpoint'), // environment variables are strings
};
function getLocalFeatureList() {
    const storage = window.localStorage.getItem(LOCAL_FEATURE_STORAGE_KEY);
    if (typeof storage !== 'string')
        return []; // no, or invalid, storage
    try {
        const parsed = JSON.parse(storage);
        if (!Array.isArray(parsed))
            throw new TypeError('Expected array');
        const featureSet = {};
        parsed.forEach((p) => {
            if (!isLocalFeature(p)) {
                console.warn(`Skipped parsing invalid feature: ${JSON.stringify(p)}`);
                return;
            }
            if (featureSet[p.name]) { // deduplicate
                console.warn(`Overriding existing ${JSON.stringify(featureSet[p.name])} with ${JSON.stringify(p)}`);
            }
            featureSet[p.name] = p;
        });
        return Object.values(featureSet);
    }
    catch (e) {
        console.error('Failed to parse local toggle database', e);
        return [];
    }
}
const remoteFeatureList = new Set();
/**
 * Add remote features into the app
 *
 * @param {Array} features
 */
export function updateRemoteToggles(features) {
    remoteFeatureList.clear();
    if (!features || !Array.isArray(features)) {
        console.warn('Invalid remote feature list', features);
        return;
    }
    features.forEach((f) => {
        if (!isFeature(f)) {
            console.warn('Invalid feature toggled by remote', f);
            return;
        }
        remoteFeatureList.add(f);
    });
}
/**
 * Returns true if the feature is enabled via either TOGGLED_FEATURES, or local storage
 *
 * @param {String} name Name of the feature
 */
export function isEnabled(name) {
    if (COMPILED_FEATURES[name])
        return true;
    const remoteEnabled = remoteFeatureList.has(name);
    if (remoteEnabled)
        return true;
    // toggle-pilot is disabled; ignore local feature list
    if (JSON.parse(window.localStorage.getItem(IGNORE_LOCAL_FEATURE_STORAGE_KEY) ?? 'false')) {
        return false;
    }
    const localFeatures = getLocalFeatureList();
    const feature = localFeatures.find((f) => f.name === name);
    return !!(feature && feature.enabled);
}
/**
 * Set the state of a feature and save it to local storage
 *
 * @param {String} name Name of the feature
 * @param {Boolean} enabled True or false
 */
export function setState(name, enabled) {
    const localFeatures = getLocalFeatureList();
    const feature = localFeatures.find((f) => f.name === name);
    if (feature) {
        feature.enabled = enabled;
    }
    else {
        localFeatures.push({ name, enabled });
    }
    window.localStorage.setItem(LOCAL_FEATURE_STORAGE_KEY, JSON.stringify(localFeatures));
}
/**
 * Shorthand function for setState(name, true);
 *
 * @param {String} name Name of the feature
 */
export function enable(name) {
    setState(name, true);
}
/**
 * Shorthand function for setState(name, false);
 *
 * @param {String} name Name of the feature
 */
export function disable(name) {
    setState(name, false);
}
/**
 * List all features in an object list of { name, enabled }
 *
 * @param {Boolean} init If true, use to init state (no console, or remote enabled hint)
 */
export function list(init) {
    const currentToggles = getLocalFeatureList();
    const sortedKeys = Object.values(Features).sort();
    const featureList = sortedKeys.map((name) => {
        const featureMatched = currentToggles && currentToggles.find((f) => f.name === name);
        return {
            name,
            enabled: !!(featureMatched && featureMatched.enabled),
            // exclude remote enabled from save
            remoteEnabled: init ? undefined : remoteFeatureList.has(name),
            compileEnabled: !!COMPILED_FEATURES[name],
        };
    });
    if (!init) {
        console.table(featureList); // eslint-disable-line no-console
    }
    return featureList;
}
/**
 * Feature plugin that makes checking for enabled/disabled features a lot easier from components,
 * as the methods will now be global
 */
export const FeaturesPlugin = {
    install: function install(Vue) {
        /**
         * Can be used from a template as:
         * <element v-if="$featureEnabled('feature-name')" />
         *
         * Or from a method:
         * if (this.$featureEnabled('feature-name')) {
         *   ...
         * }
         *
         * @param {String} featureName
         */
        // eslint-disable-next-line no-param-reassign
        Vue.prototype.$featureEnabled = function $featureEnabled(featureName) {
            return isEnabled(featureName);
        };
        /**
         * Reverse of $featureEnabled, just in case there is a use case where this is useful
         * @param {String} featureName
         */
        // eslint-disable-next-line no-param-reassign
        Vue.prototype.$featureDisabled = function $featureEnabled(featureName) {
            return !isEnabled(featureName);
        };
    },
};
// Add Feature to the window so that they can be toggled from the console or TogglePilot
const WindowFeature = {
    isEnabled,
    setState,
    enable,
    disable,
    list,
};
window.Feature = WindowFeature;
// set all keys in local storage for toggle-pilot
window.localStorage.setItem(LOCAL_FEATURE_STORAGE_KEY, JSON.stringify(list(true)));
